import { useCallback, useEffect, useRef, useState } from "react";
import { SpeechRecognition, speechLang } from "./AuraProvider";

const HOLD_MS = 350; // pressed longer than this: push-to-talk (release stops); shorter: a tap toggles recording

// Dictation into the text box (no voice call): hold the mic and speak, or tap to start and tap again to stop.
// Words appear in the draft as they're recognised; nothing is sent until the visitor presses Send.
//   draft / setDraft: the composer's text. lang: the conversation's language ("en", "hi", "ta"…).
export function useDictation({ draft, setDraft, lang, disabled = false }) {
  const [recording, setRecording] = useState(false);
  const [denied, setDenied] = useState(false);
  const recRef = useRef(null);
  const pressedAtRef = useRef(0);
  const wasRecordingRef = useRef(false); // recording was already on when this press began
  const draftRef = useRef(draft);
  draftRef.current = draft;

  const stop = useCallback(() => {
    const rec = recRef.current;
    if (rec) rec.stop(); // onend clears the state once the last words are in
  }, []);

  const start = useCallback(() => {
    if (!SpeechRecognition || disabled || recRef.current) return;
    const rec = new SpeechRecognition();
    rec.lang = speechLang(lang);
    rec.interimResults = true;
    rec.continuous = true;
    const base = draftRef.current.trim() ? `${draftRef.current.trimEnd()} ` : "";
    rec.onresult = (e) => {
      const heard = Array.from(e.results).map((r) => r[0].transcript).join("");
      setDraft(base + heard.trimStart());
    };
    rec.onerror = (e) => {
      if (e.error === "not-allowed" || e.error === "service-not-allowed") setDenied(true);
    };
    rec.onend = () => {
      if (recRef.current === rec) recRef.current = null;
      setRecording(false);
    };
    recRef.current = rec;
    setDenied(false);
    setRecording(true);
    try {
      rec.start();
    } catch {
      recRef.current = null;
      setRecording(false);
    }
  }, [disabled, lang, setDraft]);

  // A call starting (or the composer closing) ends dictation.
  useEffect(() => {
    if (disabled) stop();
  }, [disabled, stop]);
  useEffect(() => () => recRef.current?.abort(), []);

  // Props for the mic button: press-and-hold, tap-to-toggle, and Enter/Space for keyboard users.
  const buttonProps = {
    onPointerDown: (e) => {
      if (e.button !== 0) return;
      pressedAtRef.current = Date.now();
      wasRecordingRef.current = Boolean(recRef.current);
      if (!wasRecordingRef.current) start(); // a second tap is handled on release
    },
    onPointerUp: () => {
      if (!pressedAtRef.current) return;
      const held = Date.now() - pressedAtRef.current;
      pressedAtRef.current = 0;
      // Second tap stops; a hold stops on release; a quick first tap keeps recording until the next tap.
      if (wasRecordingRef.current || held >= HOLD_MS) stop();
    },
    onPointerLeave: () => {
      // Sliding off the button while holding ends push-to-talk (a quick tap's recording carries on).
      if (pressedAtRef.current && Date.now() - pressedAtRef.current >= HOLD_MS) {
        pressedAtRef.current = 0;
        stop();
      }
    },
    onClick: (e) => {
      if (e.detail !== 0) return; // mouse and touch are handled by the pointer events
      if (recRef.current) stop();
      else start();
    },
    onContextMenu: (e) => e.preventDefault(), // a long press on phones shouldn't open the menu
    "aria-pressed": recording,
  };

  return { supported: Boolean(SpeechRecognition), recording, denied, buttonProps, stop };
}
